import { QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  GET_ACTIVE_SERVICE_MESSAGE_QUERY_KEY,
  GET_SERVICE_MESSAGE_QUERY_KEY,
  SERVICE_MESSAGE_API_URL,
  SERVICE_MESSAGE_QUERY_KEY
} from '@/constants';
import { apiUrl } from '@/helpers';
import { ErrorResponse, Options } from '@/types';
import { CreateEditServiceMessage } from '@/types/ServiceMessageTypes';

export const serviceMessageEndpoints = {
  getServiceMessage: () => axios.get(`${apiUrl()}${SERVICE_MESSAGE_API_URL}`),
  getActiveServiceMessage: (signal?: AbortSignal) =>
    axios.get(`${apiUrl()}${SERVICE_MESSAGE_API_URL}/getactive`, { signal }),
  deleteServiceMessage: (id: number) => axios.delete(`${apiUrl()}${SERVICE_MESSAGE_API_URL}/${id}`),
  createServiceMessage: (payload: CreateEditServiceMessage) =>
    axios.post(`${apiUrl()}${SERVICE_MESSAGE_API_URL}`, payload),
  updateServiceMessage: (payload: CreateEditServiceMessage) =>
    axios.put(`${apiUrl()}${SERVICE_MESSAGE_API_URL}`, payload)
};

export const useCreateServiceMessageMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(serviceMessageEndpoints.createServiceMessage, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([SERVICE_MESSAGE_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useDeleteServiceMessageMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(serviceMessageEndpoints.deleteServiceMessage, {
    onSuccess: () => {
      options?.onSuccess();
      queryClient.invalidateQueries([SERVICE_MESSAGE_QUERY_KEY]);
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useGetServiceMessage = (options?: Options) => {
  return useQuery(
    [GET_SERVICE_MESSAGE_QUERY_KEY],
    () => serviceMessageEndpoints.getServiceMessage(),
    {
      retry: false,
      onSuccess: () => {
        options?.onSuccess();
      },
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useGetActiveServiceMessage = (enabled = true, options?: Options) => {
  return useQuery(
    [GET_ACTIVE_SERVICE_MESSAGE_QUERY_KEY],
    ({ signal }) => serviceMessageEndpoints.getActiveServiceMessage(signal),
    {
      enabled,
      retry: false,
      onSuccess: () => {
        options?.onSuccess();
      },
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useUpdateServiceMessageMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(serviceMessageEndpoints.updateServiceMessage, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([SERVICE_MESSAGE_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
